import { layoutTokens, typographyTokens } from "@/shared/theme";
import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { principlePresentation } from "@chessla/shared";
import { PrincipleGlyph } from "@/shared/components";
import type { PrinciplePatternExample, PrinciplePatternStat } from "../patterns.api";
import { DetectedSignalRail } from "./EvidenceRail";
import { MicroLabel } from "./PatternTypography";

export const PrinciplePatterns = ({
  patterns,
  selected = null,
  onSelectExample,
}: {
  patterns: PrinciplePatternStat[];
  /** Principle whose game lesson is open below. */
  selected?: string | null;
  onSelectExample: (example: PrinciplePatternExample) => void;
}) => {
  if (!patterns.length) return null;
  return (
    <Box
      component="section"
      data-testid="principle-patterns"
      aria-labelledby="principle-patterns-title"
      sx={{ mt: 3, borderTop: layoutTokens.structuralBorder, borderColor: "primary.main", pt: 2 }}
    >
      <MicroLabel>Decision themes / verified recurrence</MicroLabel>
      <Typography
        id="principle-patterns-title"
        component="h2"
        sx={{
          mt: 0.5,
          fontSize: { xs: "h5.fontSize", md: "h4.fontSize" },
          fontWeight: "fontWeightBold",
          letterSpacing: typographyTokens.tracking.heading,
        }}
      >
        PRINCIPLE SIGNALS
      </Typography>
      <Typography
        sx={{
          mt: 0.75,
          maxWidth: layoutTokens.readingMaxWidth,
          color: "text.secondary",
          fontSize: "caption.fontSize",
          lineHeight: typographyTokens.lineHeight.body,
        }}
      >
        Applied and missed principles from your own moves in this exact format and window. A theme appears only after
        missed evidence recurs across two games.
      </Typography>
      <Box
        sx={{
          mt: 1.5,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" },
          borderTop: layoutTokens.dividerBorder,
          borderLeft: layoutTokens.dividerBorder,
          borderColor: "divider",
        }}
      >
        {patterns.map((pattern) => {
          const presentation = principlePresentation(pattern.principle);
          const example = pattern.examples[0];
          const viewing = pattern.principle === selected;
          return (
            <Box
              key={pattern.principle}
              data-viewing={viewing || undefined}
              sx={{
                minWidth: 0,
                p: 1.5,
                borderRight: layoutTokens.dividerBorder,
                borderBottom: layoutTokens.dividerBorder,
                borderColor: "divider",
                // The open lesson's card gets a soft tint and outline, matching the watched tracked card.
                bgcolor: (theme) =>
                  viewing ? alpha(theme.palette.secondary.main, 0.06) : theme.palette.background.default,
                boxShadow: (theme) =>
                  viewing ? `inset 0 0 0 ${layoutTokens.structuralBorder}px ${theme.palette.secondary.main}` : "none",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `${layoutTokens.controlHeight}px 1fr auto`,
                  gap: 1.25,
                  alignItems: "center",
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: layoutTokens.controlHeight,
                    height: layoutTokens.controlHeight,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "background.default",
                    color: "secondary.main",
                    border: `${layoutTokens.structuralBorder}px solid currentColor`,
                  }}
                >
                  <PrincipleGlyph principle={pattern.principle} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "body2.fontSize",
                      fontWeight: "fontWeightBold",
                      letterSpacing: typographyTokens.tracking.heading,
                    }}
                  >
                    {presentation.name}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "caption.fontSize" }}>
                    {pattern.affectedGames} / {pattern.volume.analyzedGames} analyzed games · {pattern.distinctDates}{" "}
                    dates
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: "secondary.main",
                    fontSize: "caption.fontSize",
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.label,
                  }}
                >
                  {pattern.volume.maturity.toUpperCase()} EVIDENCE
                </Typography>
              </Box>
              <DetectedSignalRail
                missed={pattern.missedCount}
                applied={pattern.appliedCount}
                interval={pattern.volume.interval95}
              />
              <Typography
                sx={{
                  mt: 1,
                  color: "text.secondary",
                  fontSize: "caption.fontSize",
                  lineHeight: typographyTokens.lineHeight.body,
                }}
              >
                {presentation.cue}
              </Typography>
              {example && (
                <ButtonBase
                  onClick={() => onSelectExample(example)}
                  aria-pressed={viewing}
                  sx={{
                    mt: 1,
                    minHeight: layoutTokens.controlHeight,
                    width: "100%",
                    justifyContent: "space-between",
                    borderTop: layoutTokens.dividerBorder,
                    borderColor: "divider",
                    color: "primary.main",
                    fontSize: "caption.fontSize",
                    fontWeight: "fontWeightBold",
                    letterSpacing: typographyTokens.tracking.label,
                  }}
                >
                  <span>YOUR GAME AGAINST {example.opponent.toUpperCase()}</span>
                  <span>{viewing ? "VIEWING BOARD ↓" : "INSPECT BOARD ↓"}</span>
                </ButtonBase>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
